'use client';

import type { BrandsPickerResponse } from '@zealot/shared/apiClient';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import type React from 'react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  CommandEmpty as CommandEmptyJsx,
  CommandGroup as CommandGroupJsx,
  CommandInput as CommandInputJsx,
  CommandItem as CommandItemJsx,
  Command as CommandJsx,
  CommandList as CommandListJsx,
} from '@/components/ui/command';
import { Popover, PopoverContent as PopoverContentJsx, PopoverTrigger } from '@/components/ui/popover';
import { useBrandPicker } from '@/hooks/queries/useBrandPicker';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store';

// gloss
const Command = CommandJsx as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>;
const CommandEmpty = CommandEmptyJsx as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>;
const CommandGroup = CommandGroupJsx as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>;
const CommandInput = CommandInputJsx as React.ComponentType<Record<string, unknown>>;
const CommandItem = CommandItemJsx as React.ComponentType<
  React.PropsWithChildren<{ onSelect: () => void; key?: string }>
>;
const CommandList = CommandListJsx as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>;
const PopoverContent = PopoverContentJsx as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>;

// gloss
const BrandPicker = () => {
  const { data: brands = [], isLoading } = useBrandPicker();
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const handleSelect = (uuid: string) => {
    const brand = brands.find((b) => b.uuid === uuid);
    if (!brand?.uuid) return;

    setSelectedUuid(uuid);
    const { hubBrand, brandTheme } = useAppStore.getState();
    hubBrand.clear();
    brandTheme.clear();
    hubBrand.setHubBrand({ uuid: brand.uuid, brandName: brand.brandName });
    window.location.reload();
  };

  const selectedBrand = useMemo(() => brands.find((b) => b.uuid === selectedUuid), [brands, selectedUuid]);

  const brandItems = useMemo(
    () =>
      brands
        .filter((brand): brand is BrandsPickerResponse & { uuid: string } => Boolean(brand.uuid))
        .map((brand) => (
          <CommandItem key={brand.uuid} onSelect={() => handleSelect(brand.uuid)}>
            <span className="flex-1">{brand.brandName}</span>
            <CheckIcon className={cn('h-4 w-4', brand.uuid === selectedUuid ? 'opacity-100' : 'opacity-0')} />
          </CommandItem>
        )),
    [brands, selectedUuid],
  );

  if (isLoading) return null;

  if (brands.length === 0) {
    return <p className="mt-4 text-sm text-gray-500">No brands available.</p>;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="mt-4 w-64 justify-between">
          {selectedBrand?.brandName ?? 'Select a brand...'}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0">
        <Command>
          <CommandInput placeholder="Search brand..." />
          <CommandEmpty>No brands found.</CommandEmpty>
          <CommandList>
            <CommandGroup>{brandItems}</CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default BrandPicker;
